import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Button,
  Alert,
  Tabs,
  Tab,
  Checkbox,
} from '@mui/material';
import { Save as SaveIcon } from '@mui/icons-material';
import { providersAPI, regionsAPI, disksAPI, pricingAPI } from '../../../services/api';
import { formatDiskSize } from '../../../utils/formatCurrency';
import { Provider, Region, Disk, PowerDiskPricingRow } from '../../../types';

interface PriceChangeMap {
  [pricingId: string]: { monthlyCost?: number; isAvailable?: boolean };
}

const PowerDiskPricing = () => {
  const [providers, setProviders] = useState<Provider[]>([]);
  const [regions, setRegions] = useState<Region[]>([]);
  const [disks, setDisks] = useState<Disk[]>([]);
  const [pricing, setPricing] = useState<PowerDiskPricingRow[]>([]);
  const [selectedProvider, setSelectedProvider] = useState(0);
  const [changes, setChanges] = useState<PriceChangeMap>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const fetchInitialData = async () => {
    try {
      const [providersRes, disksRes] = await Promise.all([
        providersAPI.getAll(),
        disksAPI.getAll(),
      ]);
      const ibmProviders = providersRes.data.filter((p: Provider) => p.IsActive && p.ProviderName.toLowerCase().includes('ibm'));
      setProviders(ibmProviders);
      setDisks(disksRes.data.filter((d: Disk) => d.IsActive));
    } catch {
      setError('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const fetchPricingData = useCallback(async () => {
    if (providers.length === 0) return;

    try {
      const currentProvider = providers[selectedProvider];
      if (!currentProvider) return;
      const [pricingRes, regionsRes] = await Promise.all([
        pricingAPI.getPowerDiskPricing(currentProvider.ProviderId),
        regionsAPI.getAll(currentProvider.ProviderId),
      ]);

      setPricing(pricingRes.data);
      setRegions(regionsRes.data.filter((r: Region) => r.IsActive));
      setChanges({});
    } catch {
      setError('Failed to load pricing');
    }
  }, [providers, selectedProvider]);

  useEffect(() => {
    fetchInitialData();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (providers.length > 0) {
      fetchPricingData();
    }
  }, [selectedProvider, providers, fetchPricingData]);

  const handlePriceChange = (pricingId: number | undefined, field: string, value: number | boolean) => {
    if (pricingId === undefined) return;
    setChanges({
      ...changes,
      [pricingId]: {
        ...changes[pricingId],
        [field]: value,
      },
    });
  };

  const getPriceValue = (diskId: number, regionId: number) => {
    const pricingEntry = pricing.find(
      (p) => p.DiskId === diskId && p.RegionId === regionId
    );
    if (!pricingEntry) return '';

    const change = changes[pricingEntry.PowerDiskPricingId];
    if (change && 'monthlyCost' in change) {
      return change.monthlyCost;
    }
    return pricingEntry.MonthlyCost;
  };

  const getAvailableValue = (diskId: number, regionId: number) => {
    const pricingEntry = pricing.find(
      (p) => p.DiskId === diskId && p.RegionId === regionId
    );
    if (!pricingEntry) return false;

    const change = changes[pricingEntry.PowerDiskPricingId];
    if (change && 'isAvailable' in change) {
      return change.isAvailable;
    }
    return !!pricingEntry.IsAvailable;
  };

  const handleSave = async () => {
    try {
      const updates = Object.entries(changes).map(([pricingId, changedFields]) => {
        const originalPricing = pricing.find(p => p.PowerDiskPricingId === parseInt(pricingId));
        const mergedData = {
          monthlyCost: changedFields.monthlyCost !== undefined
            ? changedFields.monthlyCost
            : originalPricing?.MonthlyCost ?? 0,
          isAvailable: changedFields.isAvailable !== undefined
            ? changedFields.isAvailable
            : originalPricing?.IsAvailable ?? 0,
        };
        return pricingAPI.updatePowerDiskPrice(pricingId, mergedData);
      });

      await Promise.all(updates);
      setSuccess(`Updated ${Object.keys(changes).length} pricing entries`);
      setTimeout(() => setSuccess(''), 3000);
      fetchPricingData();
    } catch {
      setError('Failed to save pricing');
    }
  };

  const handleToggleAll = (diskId: number, value: boolean) => {
    const newChanges: PriceChangeMap = { ...changes };
    pricing.filter(p => p.DiskId === diskId).forEach(entry => {
      newChanges[entry.PowerDiskPricingId] = {
        ...newChanges[entry.PowerDiskPricingId],
        isAvailable: value,
      };
    });
    setChanges(newChanges);
  };

  if (loading) return <Typography>Loading...</Typography>;

  if (providers.length === 0) return (
    <Box>
      <Typography variant="h4" sx={{ mb: 2 }}>IBM Power Disk Pricing</Typography>
      <Alert severity="info">No IBM providers configured. Power disk pricing is only available for IBM Cloud providers.</Alert>
    </Box>
  );

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">IBM Power Disk Pricing</Typography>
        <Button
          variant="contained"
          color="secondary"
          startIcon={<SaveIcon />}
          onClick={handleSave}
          disabled={Object.keys(changes).length === 0}
        >
          Save Changes {Object.keys(changes).length > 0 && `(${Object.keys(changes).length})`}
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      <Paper>
        <Tabs value={selectedProvider} onChange={(_e, v: number) => setSelectedProvider(v)}>
          {providers.map((provider) => (
            <Tab key={provider.ProviderId} label={provider.ProviderName} />
          ))}
        </Tabs>

        <Box sx={{ p: 3 }}>
          {disks.map((disk) => (
            <Paper key={disk.DiskId} sx={{ p: 2, mb: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="h6">
                  {formatDiskSize(disk.DiskSize)}
                </Typography>
                <Box sx={{ display: 'flex', gap: 0.5 }}>
                  <Button size="small" variant="outlined" color="success" onClick={() => handleToggleAll(disk.DiskId, true)}>Enable All</Button>
                  <Button size="small" variant="outlined" color="error" onClick={() => handleToggleAll(disk.DiskId, false)}>Disable All</Button>
                </Box>
              </Box>

              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Region</TableCell>
                      {regions.map((region) => (
                        <TableCell key={region.RegionId} align="center">
                          {region.RegionName}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    <TableRow>
                      <TableCell>Monthly Cost</TableCell>
                      {regions.map((region) => {
                        const pricingEntry = pricing.find(
                          (p) => p.DiskId === disk.DiskId && p.RegionId === region.RegionId
                        );
                        return (
                          <TableCell key={region.RegionId} align="center">
                            <TextField
                              type="number"
                              size="small"
                              value={getPriceValue(disk.DiskId, region.RegionId)}
                              onChange={(e) => {
                                const value = e.target.value === '' ? 0 : parseFloat(e.target.value);
                                handlePriceChange(pricingEntry?.PowerDiskPricingId, 'monthlyCost', value);
                              }}
                              inputProps={{ step: '0.01', min: '0' }}
                              sx={{
                                width: '100px',
                                '& .MuiInputBase-input': {
                                  fontSize: '0.875rem',
                                  py: 0.5,
                                  px: 1,
                                },
                              }}
                            />
                          </TableCell>
                        );
                      })}
                    </TableRow>
                    <TableRow>
                      <TableCell>Enabled</TableCell>
                      {regions.map((region) => {
                        const pricingEntry = pricing.find(
                          (p) => p.DiskId === disk.DiskId && p.RegionId === region.RegionId
                        );
                        return (
                          <TableCell key={region.RegionId} align="center">
                            <Checkbox
                              checked={getAvailableValue(disk.DiskId, region.RegionId)}
                              onChange={(e) =>
                                handlePriceChange(pricingEntry?.PowerDiskPricingId, 'isAvailable', e.target.checked)
                              }
                            />
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          ))}
        </Box>
      </Paper>
    </Box>
  );
};

export default PowerDiskPricing;
